import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import { RevealOnce } from 'common/components/RevealOnce';
import { EmailDetails, SendoverReasonsFields } from 'features/sendouts/components/forms';
import { AutomaticReminder } from 'features/sendouts/components/forms/AutomaticReminder';
import { getSelectedReasonIds } from 'features/sendouts/components/forms/SendoverReasonsFields';
import {
  AccountableRecruiterSelector,
  Attachments,
  CandidateDetailsFields
} from 'features/sendouts/components/shared';
import { ATTACHMENTS_CATEGORIES } from 'features/sendouts/constants';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { Permissions, userHasPermission } from 'services/Authorization';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import NumberedForm from 'UI/components/molecules/NumberedForm';
import { CandidateFileCategoriesWithSendout } from 'UI/components/organisms/sendouts/utils';
import { Endpoints } from 'UI/constants/endpoints';
import { FeatureFlags } from 'UI/constants/featureFlags';

import { NumberedFormWrapper } from '../../CreateSendoutDrawer/SendoutForm/SendoutForm.styled';
import {
  ATTACHMENT_FIELD_KEYS,
  buildAttachmentFieldValues
} from '../../CreateSendoutDrawer/SendoutForm/SendoutForm.utils';

import { FORM_FIELDS_MAP, SENDOVER_TEMPLATE_ID } from './SendoverForm.constants';

export const SendoverForm = ({
  candidateAttachments = [],
  candidateInfo = null,
  duplicateInfo = { isDuplicate: false },
  hydrateCandidate,
  isHydratingCandidate = false,
  jobOrder = {},
  recruiterOptions = [],
  onViewDuplicates = () => {}
}) => {
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const isAutomaticReminderEnabled = checkIfFeatureFlagEnabled(FeatureFlags.SendoverHAReminder);

  const jobOrderId = jobOrder?.id;

  const { errors, getValues, setValue, watch } = useFormContext();

  const formValues = watch([
    FORM_FIELDS_MAP.AccountableRecruiter.key,
    FORM_FIELDS_MAP.Candidate.key,
    FORM_FIELDS_MAP.SendoverReasons.key
  ]);

  const accountableRecruiterValue = formValues[FORM_FIELDS_MAP.AccountableRecruiter.key];
  const candidateValue = formValues[FORM_FIELDS_MAP.Candidate.key];
  const candidateId = candidateValue?.id;
  const sendoverReasonsValue = formValues[FORM_FIELDS_MAP.SendoverReasons.key];

  const shouldDisplayAutomaticReminderCheckbox =
    isAutomaticReminderEnabled &&
    userHasPermission(Permissions.Sendouts.CanOverrideSendoverHAFollowUp) &&
    candidateValue;

  const shouldDisplayAutomaticReminderCopy = isAutomaticReminderEnabled && candidateValue;

  const syncAttachmentFields = useCallback(
    attachments => {
      const attachmentFieldValues = buildAttachmentFieldValues(attachments);

      ATTACHMENT_FIELD_KEYS.forEach(fieldKey => {
        setValue(fieldKey, attachmentFieldValues[fieldKey], false);
      });
    },
    [setValue]
  );

  const handleOnSelectCandidate = useCallback(
    async candidate => {
      const requestedCandidateId = candidate?.id;
      const { attachments, resolvedRecruiter } = await hydrateCandidate({
        candidateId: requestedCandidateId
      });

      const currentCandidate = getValues(FORM_FIELDS_MAP.Candidate.key);
      const isStaleCandidateSelection = currentCandidate?.id !== requestedCandidateId;
      if (isStaleCandidateSelection) {
        return;
      }

      syncAttachmentFields(attachments);
      setValue(FORM_FIELDS_MAP.CandidateRecruiter.key, resolvedRecruiter, false);
    },
    [getValues, hydrateCandidate, setValue, syncAttachmentFields]
  );

  return (
    <Grid container>
      <Grid item xs={12}>
        <Box display="flex" flexDirection="column" mb={2}>
          <Typography variant="subtitle1">
            {strings.sendoutsV2.form.sections.accountableSelector.title}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {strings.sendoutsV2.form.sections.accountableSelector.subtitle}
          </Typography>
        </Box>
        <AccountableRecruiterSelector jobOrderId={jobOrderId} />
      </Grid>
      <NumberedFormWrapper item xs={12}>
        <NumberedForm>
          <When condition={accountableRecruiterValue}>
            <NumberedForm.Item title={strings.sendoutsV2.form.sections.candidateDetails.title}>
              <CandidateDetailsFields
                isHydratingCandidate={isHydratingCandidate}
                onSelectCandidate={handleOnSelectCandidate}
                recruiterOptions={recruiterOptions}
              />
            </NumberedForm.Item>
          </When>
          <When condition={duplicateInfo.isDuplicate}>
            <NumberedForm.Container>
              <Alert
                severity="warning"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    variant="outlined"
                    onClick={() => onViewDuplicates({ candidate: candidateValue })}
                  >
                    {strings.shared.ui.view}
                  </Button>
                }
              >
                {strings.sendoutsV2.form.sections.candidateDetails.alerts.duplicateCandidate}
              </Alert>
            </NumberedForm.Container>
          </When>
          <When condition={candidateValue}>
            <NumberedForm.Item title={strings.sendoutsV2.form.sections.sendoverReasons.title}>
              <SendoverReasonsFields />
            </NumberedForm.Item>
          </When>
          <RevealOnce
            when={getSelectedReasonIds(sendoverReasonsValue).length > 0}
            resetWhen={!candidateValue}
          >
            <NumberedForm.Item title={strings.sendoutsV2.form.sections.attachments.title}>
              <Attachments
                key={`${candidateId}-${candidateAttachments.length}`}
                categories={[ATTACHMENTS_CATEGORIES.resume, ATTACHMENTS_CATEGORIES.other]}
                categoryIdMap={CandidateFileCategoriesWithSendout}
                endpoint={Endpoints.Files}
                files={candidateAttachments}
                maxNumberOfFiles={Infinity}
              />
              <When condition={!!errors[FORM_FIELDS_MAP.Attachments.key]}>
                <Box mt={2}>
                  <Alert severity="error">{errors[FORM_FIELDS_MAP.Attachments.key]?.message}</Alert>
                </Box>
              </When>
            </NumberedForm.Item>
            <NumberedForm.Item title={strings.sendoutsV2.form.sections.emailDetails.title}>
              <AutomaticReminder
                shouldDisplayAutomaticReminderCheckbox={shouldDisplayAutomaticReminderCheckbox}
                shouldDisplayAutomaticReminderCopy={shouldDisplayAutomaticReminderCopy}
              />
              <EmailDetails
                jobOrder={jobOrder}
                candidateInfo={candidateInfo}
                templateId={SENDOVER_TEMPLATE_ID}
                canDisableEmailToHiringAuthority={false}
              />
            </NumberedForm.Item>
          </RevealOnce>
        </NumberedForm>
      </NumberedFormWrapper>
    </Grid>
  );
};
